import _ from 'underscore';

import { getAppStore } from '../../../app/AppStore';
import { NavLocationSelectors } from '../../../app/selectors/NavigationStateSelectors';
import { selectSpaceInfo } from '../../../app/selectors/SpaceInfoSelectors';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../../../app/state/SiteSpaceContentTypeState';
import {
  DatasourceSubspaceName, FlowSubspaceName, GroupSubspaceName, groupSubspaceNameToExplorableItemType,
  isBackgroundJobsSpace, isDataRoleSpace, isDatasourceSpace, isFavoritesSpace, isFlowSpace, isGroupSpace,
  isProjectSpace, isRecentsSpace, isRecommendationsSpace, isScheduleSpace, isSearchSpace, isServerTasksSpace,
  isSiteSpace, isSiteTasksSpace, isSiteUserContentSubspace, isSiteUserSpace, isUnifiedSearchSpace, isWorkbookSpace,
  ScheduleSubspaceName, scheduleSubspaceNameToExplorableItemType, SearchSubspaceName, ServerTasksSubspaceName,
  serverTasksSubspaceNameToExplorableItemType, SiteTasksSubspaceName, siteTasksSubspaceNameToExplorableItemType,
  SpaceInfo, WorkbookSubspaceName, isSharedWithMeSpace, isSingleCollectionSpace, isPersonalSpace, isCollectionsSpace, isCatalogSpace, subspaceNameToCatalogContentType
} from '../../../app/state/SpaceInfo';
import { FeatureFlags } from '../../FeatureFlags';
import { Order } from '../../helpers/Order';
import { IOrderClause } from '../../ServerApi/types/IOrderClause';
import { ExplorableItemType } from '../ContentType';
import {
  AccelerationTaskContentType,
  BackgroundJobContentType,
  CustomViewsContentType,
  DataAlertContentType,
  DatabaseContentType,
  DataConnectionContentType,
  DataConnectionInFlowContentType,
  DataRoleContentType,
  DatasourceContentType,
  ExtractTaskContentType,
  ExtractTaskSingleTargetContentType,
  FlowContentType,
  FlowOutputStepRunContentType,
  GroupContentType,
  GroupUserContentType,
  MixedContentType,
  ProjectContentType,
  PublishedConnectionContentType,
  RemoteRefreshScheduleContentType,
  RunFlowTaskContentType,
  ScheduleContentType,
  ServerUserContentType,
  SitesContentType,
  SiteUserContentType,
  SubscriptionContentType,
  TableContentType,
  ViewContentType,
  WorkbookContentType,
  WorkbookAccelerationContentType,
  DatabaseWithCRASSContentType,
  LensContentType,
  TableWithCRASSContentType,
  DatabaseWithCRASSSearchSubspaceType,
  TableWithCRASSSearchSubspaceType
} from '../ContentTypes';
import { MetricContentType } from '../../../metrics/models/MetricContentType';
import { OrderKeys } from '../OrderKeys';
import { backgroundJobsSortOptions } from './BackgroundJobsSortOptions';
import { databaseDropdownSortOptions, tableDropdownSortOptions } from './CatalogSortOptions';
import { collectionItemsDefaultSortOrder, collectionItemsMixedContentOptions, collectionItemsSpecificSortOptions } from './CollectionItemsSortOptions';
import { collectionsSortOptions, defaultCollectionSort, getCollectionsSortOptions, collectionsSortOptionsWithCollectionsGA } from './CollectionSortOptions';
import { SortOptions } from './CommonSortOptions';
import { customViewSortOptions } from './CustomViewSortOptions';
import { dataAlertSortOptions } from './DataAlertSortOptions';
import { dataConnectionSortOptions, WorkbookSubspaceDataConnectionSortOptions } from './DataConnectionSortOptions';
import { dataRoleSortOptions } from './DataRoleSortOptions';
import { datasourceSortOptions, datasourceSortOptionsForSearch } from './DatasourceSortOptions';
import { DatasourceSubspaceConnectionsSortOptions } from './DatasourceSubplaceSortOptions';
import { extractRefreshSortOptions } from './ExtractRefreshSortOptions';
import { getFavoritesMixedContentOptions, getFavoritesSpaceDefaultSortOrder, favoritesMixedContentOptions_reorderFavoritesOff } from './FavoritesSortOptions';
import { FlowConnectionsSortOptions, flowOutputStepRunSortOptions, flowSortOptions, runFlowTasksSortOptions } from './FlowSortOptions';
import { groupSortOptions } from './GroupSortOptions';
import { metricsSortOptions } from './MetricsSortOptions';
import { mixedContentInCrassDefaultSortOrder, mixedContentInCrassSortOptions } from './MixedContentInCrassSortOptions';
import { mixedContentOptions } from './MixedContentSortOptions';
import { projectSortOptions } from './ProjectSortOptions';
import { publishedConnectionSortOptions } from './PublishedConnectionSortOptions';
import { recentsSortOptions, recentsSpaceDefaultSortOrder, recentViewsOptions } from './RecentsSortOptions';
import { remoteRefreshSortOptions } from './RemoteRefreshSortOptions';
import { scheduleSortOptions } from './ScheduleSortOptions';
import { serverUserSortOptions } from './ServerUserSortOptions';
import { sharedWithMeDefaultSortOrder, sharedWithMeSortOptions } from './SharedWithMeSortOptions';
import { siteSortOptions } from './SiteSortOptions';
import { siteUserContentDefaultSortOrder, siteUserContentSortOptions } from './SiteUserContentSortOptions';
import { siteUserSortOptions } from './SiteUserSortOptions';
import { subscriptionSortOptions } from './SubscriptionSortOptions';
import { defaultUserSort, userSortOptions } from './UserSortOptions';
import { viewSortOptions } from './ViewSortOptions';
import { DataSourceSubspaceWorkbookSortOptions, workbookSortOptions } from './WorkbookSortOptions';
import { WorkbookSubspaceDatasourcesSortOptions, WorkbookSubspaceViewsSortOptions } from './WorkbookSubplaceSortOptions';
import { workbookAccelerationSortOptions } from './WorkbookAccelerationSortOptions';
import { dataAccelerationSortOptions } from './DataAccelerationSortOptions';
import { lensSortOptions } from './LensSortOptions';
import { databaseWithCRASSSortOptions } from './DatabaseWithCrassDropdownSortOptions';
import { tableWithCRASSSortOptions } from './TableWithCrassDropdownSortOptions';

export type GetDisplayValue = () => string;

export interface OptionDefinition {
  readonly id: string;
  readonly getMenuItemDisplayValue: GetDisplayValue;
  readonly field?: string;
  readonly getButtonDisplayValue?: GetDisplayValue;
  readonly ascending?: boolean;
  readonly toOrderClauseArray?: () => ReadonlyArray<IOrderClause>;
  readonly subSortOptions?: { readonly [s: string]: OptionDefinition };
}

export function getPathToOption(order: IOrderClause, options: OptionsDictionary): OptionDefinition[] {
  return dfs(options, d => d.field === order.field &&
    d.ascending === order.ascending);
}

export function hasPathToOption(order: IOrderClause, options: OptionsDictionary): boolean {
  return getPathToOption(order, options).length > 0;
}

export function getPathToOptionById(id: string, options: OptionsDictionary): OptionDefinition[] {
  return dfs(options, d => d.id === id);
}

export function removeInvalidSortClauses(order: ReadonlyArray<IOrderClause>, contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo) {
  const validOptions = getOptionsForContentTypes(contentTypes, spaceInfo);
  return _.filter(order, orderClause => hasPathToOption(orderClause, validOptions));
}

export function areValidForTypes(order: ReadonlyArray<IOrderClause>, contentTypes: ReadonlyArray<ExplorableItemType>) {
  const spaceInfo: SpaceInfo = selectSpaceInfo(getAppStore().getState());
  const options = getOptionsForContentTypes(contentTypes, spaceInfo);
  return _.all(order, orderClause => hasPathToOption(orderClause, options));
}

export function areValid(order: ReadonlyArray<IOrderClause>, contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo) {
  const options = getOptionsForContentTypes(contentTypes, spaceInfo);
  return _.all(order, orderClause => hasPathToOption(orderClause, options));
}

export function getOptionsForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): OptionsDictionary {
  if (spaceInfo && isWorkbookSpace(spaceInfo)) {
    const options = sortOptionsByWorkbookSubspace[spaceInfo.subspaceName as WorkbookSubspaceName].options;
    if (FeatureFlags.personalSpaceEnabled && spaceInfo.subspaceName as WorkbookSubspaceName === 'connectedMetrics') {
      return getSortOptionsWithContainerName(options);
    }
    return options;
  } else if (spaceInfo && isDatasourceSpace(spaceInfo)) {
    const options = sortOptionsByDatasourceSubspace[spaceInfo.subspaceName as DatasourceSubspaceName].options;
    if (FeatureFlags.personalSpaceEnabled && spaceInfo.subspaceName as DatasourceSubspaceName === 'connectedWorkbooks') {
      return getSortOptionsWithContainerName(options);
    }
    return options;
  } else if (spaceInfo && isFlowSpace(spaceInfo)) {
    return sortOptionsByFlowSubspace[spaceInfo.subspaceName as FlowSubspaceName].options;
  } else if (spaceInfo && isDataRoleSpace(spaceInfo)) {
    return {};
  } else if (spaceInfo && isSiteSpace(spaceInfo)) {
    const options = getSortOptionsByContentType(convertSiteContentTypeToContentType(spaceInfo.subspaceName as SiteSpaceContentType));
    // Note: Currently Collections is not setup to return modified by and will break with the sort / filter on.
    return !FeatureFlags.modifiedByUserEnabled ? _.omit(options, 'modifiedBy') : options;
  } else if (spaceInfo && isFavoritesSpace(spaceInfo)) {
    return getFavoritesSortOptions(contentTypes);
  } else if (spaceInfo && isRecentsSpace(spaceInfo)) {
    let options = FeatureFlags.metricsSpaceEnabled ? recentsSortOptions : recentViewsOptions;
    if (FeatureFlags.personalSpaceEnabled) {
      return getSortOptionsWithContainerName(options);
    }
    return options;
  } else if (spaceInfo && isBackgroundJobsSpace(spaceInfo)) {
    return backgroundJobsSortOptions;
  } else if (spaceInfo && isSiteTasksSpace(spaceInfo)) {
    return getSortOptionsByContentType(siteTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteTasksSubspaceName) as ExplorableItemType);
  } else if (spaceInfo && isServerTasksSpace(spaceInfo)) {
    return getSortOptionsByContentType(serverTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ServerTasksSubspaceName) as ExplorableItemType);
  } else if (spaceInfo && isUnifiedSearchSpace(spaceInfo)) {
    return mixedContentInCrassSortOptions;
  } else if (spaceInfo && isSearchSpace(spaceInfo)) {
    return getSortOptionsBySearchSubspace(spaceInfo.subspaceName as SearchSubspaceName);
  } else if (spaceInfo && isSiteUserSpace(spaceInfo)) {
    if (spaceInfo.subspaceName === 'settings') {
      return {};
    }
    return getSiteUserContentSortOptions(contentTypes);
  } else if (spaceInfo && isGroupSpace(spaceInfo)) {
    const explorableItemType = groupSubspaceNameToExplorableItemType(spaceInfo.subspaceName as GroupSubspaceName);
    if (explorableItemType === '') {
      return {};
    }
    const options = getSortOptionsByContentType(explorableItemType);
    return options;
  } else if (isScheduleSpace(spaceInfo)) {
    const explorableItemType = scheduleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ScheduleSubspaceName);
    if (explorableItemType === '') {
      return {};
    } else {
      const options = getSortOptionsByContentType(explorableItemType);
      const inServerArea = NavLocationSelectors.inServerArea(getAppStore().getState());
      if (inServerArea) {
        return { ...options, 'siteName': SortOptions.siteName };
      }
      return options;
    }
  } else if (isSharedWithMeSpace(spaceInfo)) {
    return sharedWithMeSortOptions;
  } else if (isPersonalSpace(spaceInfo)) {
    return getFavoritesSortOptions(contentTypes);
  } else if (isSingleCollectionSpace(spaceInfo)) {
    return getCollectionItemsSortOptions(contentTypes);
  } else if (isCollectionsSpace(spaceInfo)) {
    return getCollectionsSortOptions(spaceInfo);
  } else if (isCatalogSpace(spaceInfo)) {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return getSortOptionsByContentType(catalogContentType);
  } else if (contentTypes.length === 1) {
    return getSortOptionsByContentType(contentTypes[0]);
  } else if (isCatalogSpace(spaceInfo)) {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return getSortOptionsByContentType(catalogContentType);
  } else {
    return mixedContentOptions;
  }
}

function getFavoritesSortOptions(contentTypes: ReadonlyArray<ExplorableItemType>) {
  if (contentTypes.length === 1) {
    const favoritesSorts = FeatureFlags.reorderFavoritesEnabled ?
      { 'position': SortOptions.position, 'dateAdded': SortOptions.dateAdded } :
      { 'position': SortOptions.position_reorderFavoritesOff };
    return { ...getSortOptionsByContentType(contentTypes[0]), ...favoritesSorts };
  } else {
    return getFavoritesMixedContentOptions();
  }
}

function getCollectionItemsSortOptions(contentTypes: ReadonlyArray<ExplorableItemType>) {
  if (contentTypes.length === 1) {
    let customSortOptions = getSortOptionsByContentType(contentTypes[0]);
    // TFS 1235644 to remove this delete once Relevance is removed from sorting.
    // Relevance doesn't exist as a field under CRASS's /browse API and was erroring out
    // when the user sorted or filtered in Collection Items.
    customSortOptions = _.omit(customSortOptions, 'relevance');

    // Note: Currently Collections is not setup to return modified by and will break with the sort / filter on.
    customSortOptions = !FeatureFlags.modifiedByUserEnabled ? _.omit(customSortOptions, 'modifiedBy') : customSortOptions;

    // sorting on permissioned subcontent counts isn't supported in collection items
    if (contentTypes[0] === 'project') {
      customSortOptions = _.omit(customSortOptions, 'projects', 'workbooks', 'views', 'datasources');
    }

    if (contentTypes[0] === 'datasource') {
      customSortOptions = _.omit(customSortOptions, 'refresh');
    }

    return {
      ...customSortOptions,
      ...collectionItemsSpecificSortOptions
    };
  } else {
    return collectionItemsMixedContentOptions;
  }
}

function getSiteUserContentSortOptions(contentTypes: ReadonlyArray<ExplorableItemType>) {
  if (contentTypes.length === 1) {
    return _.omit(getSortOptionsByContentType(contentTypes[0]), 'owner');
  } else {
    return siteUserContentSortOptions;
  }
}

function getSortOptionsByContentType(contentType: ExplorableItemType): OptionsDictionary {
  let sortOptions = sortOptionsByContentType[contentType].options;
  if (FeatureFlags.personalSpaceEnabled &&
    (contentType === 'workbook' ||
      contentType === 'connectedWorkbook' ||
      contentType === 'dataRole' ||
      contentType === 'datasource' ||
      contentType === 'flow' ||
      contentType === 'metric' ||
      contentType === 'publishedConnection' ||
      contentType === 'view')) {

    sortOptions = getSortOptionsWithContainerName(sortOptions);
  }

  if (!FeatureFlags.collectionsGAEnabled && contentType === 'collection') {
    sortOptions = collectionsSortOptions;
  }

  return sortOptions;
}

function getSortOptionsBySearchSubspace(searchSubspaceName: SearchSubspaceName): OptionsDictionary {
  let sortOptions = sortOptionsBySearchSubspace[searchSubspaceName].options;
  if (FeatureFlags.personalSpaceEnabled &&
    (searchSubspaceName === 'workbooks' ||
      searchSubspaceName === 'dataRoles' ||
      searchSubspaceName === 'datasources' ||
      searchSubspaceName === 'flows' ||
      searchSubspaceName === 'metrics' ||
      searchSubspaceName === 'publishedConnections' ||
      searchSubspaceName === 'views')) {

    sortOptions = getSortOptionsWithContainerName(sortOptions);
  }
  return sortOptions;
}

/* Need to dynamically add container name when PS FF is ON as checking in const wont work due
to being initialized before FF is set from BE, this code should be removed and instead added statically
to const (for ex: WorkbookSortOptions.ts) when PS FF is removed */
function getSortOptionsWithContainerName(options: OptionsDictionary): OptionsDictionary {
  let optionsWithContainerName = _.omit(options, ['projectName', 'project']);
  return { ...optionsWithContainerName, ...{ 'containerName': SortOptions.containerName } };
}

export function getDisplaySortDropdownForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): boolean {
  if (spaceInfo && isWorkbookSpace(spaceInfo)) {
    return sortOptionsByWorkbookSubspace[spaceInfo.subspaceName as WorkbookSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isDatasourceSpace(spaceInfo)) {
    return sortOptionsByDatasourceSubspace[spaceInfo.subspaceName as DatasourceSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isFlowSpace(spaceInfo)) {
    return sortOptionsByFlowSubspace[spaceInfo.subspaceName as FlowSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isDataRoleSpace(spaceInfo)) {
    return false;
  } else if (spaceInfo && isRecommendationsSpace(spaceInfo)) {
    return false;
  } else if (spaceInfo && isSiteSpace(spaceInfo)) {
    return sortOptionsByContentType[convertSiteContentTypeToContentType(spaceInfo.subspaceName as SiteSpaceContentType)].displaySortDropdown;
  } else if (spaceInfo && isSearchSpace(spaceInfo)) {
    return sortOptionsBySearchSubspace[spaceInfo.subspaceName as SearchSubspaceName].displaySortDropdown;
  } else if (spaceInfo && isSharedWithMeSpace(spaceInfo)) {
    // Note: change it to true to allow SortBy option
    return false;
  } else if (contentTypes.length === 1) {
    return sortOptionsByContentType[contentTypes[0]].displaySortDropdown;
  } else if (spaceInfo && (isSiteTasksSpace(spaceInfo) || isServerTasksSpace(spaceInfo))) {
    return false;
  } else if (spaceInfo && isGroupSpace(spaceInfo)) {
    return false;
  } else if (spaceInfo && isSingleCollectionSpace(spaceInfo)) {
    return FeatureFlags.collectionsGAEnabled;
  } else if (spaceInfo && isCatalogSpace(spaceInfo)) {
    return FeatureFlags.externalAssetsWithCRASSEnabled;
  } else { // includes isFavoritesSpace and RecentsSpace
    return true;
  }
}

export function getDefaultOrderForContentTypes(contentTypes: ReadonlyArray<ExplorableItemType>): IOrderClause[] {
  return getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, null);
}

export function getDefaultOrderForSpaceInfo(spaceInfo: SpaceInfo): IOrderClause[] {
  return getDefaultOrderForContentTypesOrSpaceInfo([], spaceInfo);
}

export function getDefaultOrderForFlowSubSpace(spaceInfo: SpaceInfo): IOrderClause[] {
  return sortOptionsByFlowSubspace[spaceInfo.subspaceName as FlowSubspaceName].defaultOrder;
}

export const EmptySortOrder: IOrderClause[] = [];

export function getDefaultOrderForContentTypesOrSpaceInfo(contentTypes: ReadonlyArray<ExplorableItemType>, spaceInfo: SpaceInfo): IOrderClause[] {
  if (spaceInfo && isWorkbookSpace(spaceInfo)) {
    return sortOptionsByWorkbookSubspace[spaceInfo.subspaceName as WorkbookSubspaceName].defaultOrder;
  } else if (spaceInfo && isDatasourceSpace(spaceInfo)) {
    return sortOptionsByDatasourceSubspace[spaceInfo.subspaceName as DatasourceSubspaceName].defaultOrder;
  } else if (spaceInfo && isFlowSpace(spaceInfo)) {
    return getDefaultOrderForFlowSubSpace(spaceInfo);
  } else if (spaceInfo && isSiteSpace(spaceInfo)) {
    return sortOptionsByContentType[convertSiteContentTypeToContentType(spaceInfo.subspaceName as SiteSpaceContentType)].defaultOrder;
  } else if (spaceInfo && isFavoritesSpace(spaceInfo)) {
    return getFavoritesSpaceDefaultSortOrder();
  } else if (spaceInfo && isRecentsSpace(spaceInfo)) {
    return recentsSpaceDefaultSortOrder;
  } else if (spaceInfo && isSharedWithMeSpace(spaceInfo)) {
    return sharedWithMeDefaultSortOrder;
  } else if (spaceInfo && isSearchSpace(spaceInfo)) {
    return sortOptionsBySearchSubspace[spaceInfo.subspaceName as SearchSubspaceName].defaultOrder;
  } else if (spaceInfo && isUnifiedSearchSpace(spaceInfo)) {
    return mixedContentInCrassDefaultSortOrder;
  } else if (spaceInfo && isBackgroundJobsSpace(spaceInfo)) {
    return BackgroundJobContentType.defaultOrder;
  } else if (spaceInfo && isGroupSpace(spaceInfo)) {
    if (spaceInfo.subspaceName === 'details') {
      return EmptySortOrder;
    } else {
      return GroupUserContentType.defaultOrder;
    }
  } else if (spaceInfo && isSiteUserSpace(spaceInfo)) {
    if (spaceInfo.subspaceName === 'settings') {
      return EmptySortOrder;
    }

    if (contentTypes.length === 1) {
      return sortOptionsByContentType[contentTypes[0]].defaultOrder;
    }

    return siteUserContentDefaultSortOrder;
  } else if (isScheduleSpace(spaceInfo)) {
    const explorableItemType = scheduleSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ScheduleSubspaceName);
    if (explorableItemType === '') {
      return EmptySortOrder;
    } else {
      return sortOptionsByContentType[explorableItemType].defaultOrder;
    }
  } else if (isSiteTasksSpace(spaceInfo)) {
    const explorableItemType = siteTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as SiteTasksSubspaceName);
    if (explorableItemType === '') {
      return EmptySortOrder;
    } else {
      return sortOptionsByContentType[explorableItemType].defaultOrder;
    }
  } else if (isServerTasksSpace(spaceInfo)) {
    const explorableItemType = serverTasksSubspaceNameToExplorableItemType(spaceInfo.subspaceName as ServerTasksSubspaceName);
    if (explorableItemType === '') {
      return EmptySortOrder;
    } else {
      return sortOptionsByContentType[explorableItemType].defaultOrder;
    }
  } else if (isSingleCollectionSpace(spaceInfo)) {
    return collectionItemsDefaultSortOrder;
  } else if (contentTypes.length === 1) {
    return sortOptionsByContentType[contentTypes[0]].defaultOrder;
  } else if (contentTypes.length > 1 || isProjectSpace(spaceInfo)) {
    return MixedContentType.defaultOrder;
  } else if (isCatalogSpace(spaceInfo)) {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return sortOptionsByContentType[catalogContentType].defaultOrder;
  } else {
    return EmptySortOrder;
  }
}

export type OptionsDictionary = { readonly [s: string]: OptionDefinition };

export interface SortOptionsForType {
  defaultOrder: IOrderClause[];
  options: OptionsDictionary;
  displaySortDropdown: boolean;
}

// exported for testing
export const sortOptionsByContentType: { [s in ExplorableItemType]: SortOptionsForType } = {
  'project': { defaultOrder: ProjectContentType.defaultOrder, options: projectSortOptions, displaySortDropdown: true },
  'workbook': { defaultOrder: WorkbookContentType.defaultOrder, options: workbookSortOptions, displaySortDropdown: true },
  'datasource': { defaultOrder: DatasourceContentType.defaultOrder, options: datasourceSortOptions, displaySortDropdown: true },
  'dataRole': { defaultOrder: DataRoleContentType.defaultOrder, options: dataRoleSortOptions, displaySortDropdown: true },
  'lens': { defaultOrder: LensContentType.defaultOrder, options: lensSortOptions, displaySortDropdown: true },
  'view': { defaultOrder: ViewContentType.defaultOrder, options: viewSortOptions, displaySortDropdown: true },
  'customView': { defaultOrder: CustomViewsContentType.defaultOrder, options: customViewSortOptions, displaySortDropdown: false },
  'flow': { defaultOrder: FlowContentType.defaultOrder, options: flowSortOptions, displaySortDropdown: true },
  'flowOutputStepRun': { defaultOrder: FlowOutputStepRunContentType.defaultOrder, options: flowOutputStepRunSortOptions, displaySortDropdown: false },
  'dataconnection': { defaultOrder: DataConnectionContentType.defaultOrder, options: dataConnectionSortOptions, displaySortDropdown: true },
  'subscription': { defaultOrder: SubscriptionContentType.defaultOrder, options: subscriptionSortOptions, displaySortDropdown: false },
  'extractTask': { defaultOrder: ExtractTaskContentType.defaultOrder, options: extractRefreshSortOptions, displaySortDropdown: false },
  'workbookAcceleration': { defaultOrder: WorkbookAccelerationContentType.defaultOrder, options: workbookAccelerationSortOptions, displaySortDropdown: false },
  'connection': { defaultOrder: DataConnectionContentType.defaultOrder, options: dataConnectionSortOptions, displaySortDropdown: true },
  'remoteRefreshSchedule': { defaultOrder: RemoteRefreshScheduleContentType.defaultOrder, options: remoteRefreshSortOptions, displaySortDropdown: false },
  'connectedWorkbook': { defaultOrder: WorkbookContentType.defaultOrder, options: workbookSortOptions, displaySortDropdown: true },
  'backgroundJob': { defaultOrder: BackgroundJobContentType.defaultOrder, options: backgroundJobsSortOptions, displaySortDropdown: true },
  'databaseWithCrass': { defaultOrder: DatabaseWithCRASSContentType.defaultOrder, options: databaseWithCRASSSortOptions, displaySortDropdown: true },
  'tableWithCrass': { defaultOrder: TableWithCRASSContentType.defaultOrder, options: tableWithCRASSSortOptions, displaySortDropdown: true },
  'database': { defaultOrder: DatabaseContentType.defaultOrder, options: databaseDropdownSortOptions, displaySortDropdown: false },
  'table': { defaultOrder: TableContentType.defaultOrder, options: tableDropdownSortOptions, displaySortDropdown: false },
  'metric': { defaultOrder: MetricContentType.defaultOrder, options: metricsSortOptions, displaySortDropdown: true },
  'siteuser': { defaultOrder: SiteUserContentType.defaultOrder, options: siteUserSortOptions, displaySortDropdown: false },
  'serveruser': { defaultOrder: ServerUserContentType.defaultOrder, options: serverUserSortOptions, displaySortDropdown: false },
  'searchuser': { defaultOrder: defaultUserSort, options: userSortOptions, displaySortDropdown: false },
  'recommendedView': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'dataalert': { defaultOrder: DataAlertContentType.defaultOrder, options: dataAlertSortOptions, displaySortDropdown: false },
  'site': { defaultOrder: SitesContentType.defaultOrder, options: siteSortOptions, displaySortDropdown: false },
  'schedule': { defaultOrder: ScheduleContentType.defaultOrder, options: scheduleSortOptions, displaySortDropdown: false },
  'runFlowTask': { defaultOrder: RunFlowTaskContentType.defaultOrder, options: runFlowTasksSortOptions, displaySortDropdown: false },
  'group': { defaultOrder: GroupContentType.defaultOrder, options: groupSortOptions, displaySortDropdown: false },
  'collection': { defaultOrder: defaultCollectionSort, options: collectionsSortOptionsWithCollectionsGA, displaySortDropdown: true },
  'publishedConnection': { defaultOrder: PublishedConnectionContentType.defaultOrder, options: publishedConnectionSortOptions, displaySortDropdown: true },
  'accelerationTask': { defaultOrder: AccelerationTaskContentType.defaultOrder, options: dataAccelerationSortOptions, displaySortDropdown: false },
};

export const sortOptionsByWorkbookSubspace: { [s in WorkbookSubspaceName]: SortOptionsForType } = {
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'datasources': WorkbookSubspaceDatasourcesSortOptions,
  'customViews': { defaultOrder: CustomViewsContentType.defaultOrder, options: customViewSortOptions, displaySortDropdown: false },
  'dataconnections': { defaultOrder: DataConnectionContentType.defaultOrder, options: WorkbookSubspaceDataConnectionSortOptions, displaySortDropdown: true },
  'views': WorkbookSubspaceViewsSortOptions,
  'subscriptions': { defaultOrder: SubscriptionContentType.defaultOrder, options: subscriptionSortOptions, displaySortDropdown: false },
  'extractTasks': { defaultOrder: ExtractTaskSingleTargetContentType.defaultOrder, options: extractRefreshSortOptions, displaySortDropdown: false },
  'connectedMetrics': { defaultOrder: MetricContentType.defaultOrder, options: metricsSortOptions, displaySortDropdown: true },
  'workbookAccelerations': { defaultOrder: WorkbookAccelerationContentType.defaultOrder, options: workbookAccelerationSortOptions, displaySortDropdown: false },
};

const sortOptionsByDatasourceSubspace: { [s in DatasourceSubspaceName]: SortOptionsForType } = {
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'connections': { defaultOrder: DatasourceSubspaceConnectionsSortOptions.defaultOrder, options: DatasourceSubspaceConnectionsSortOptions.options, displaySortDropdown: true },
  'extractRefreshes': { defaultOrder: ExtractTaskSingleTargetContentType.defaultOrder, options: extractRefreshSortOptions, displaySortDropdown: false },
  'nlp': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'remoteRefreshSchedules': { defaultOrder: RemoteRefreshScheduleContentType.defaultOrder, options: remoteRefreshSortOptions, displaySortDropdown: false },
  'connectedWorkbooks': { defaultOrder: WorkbookContentType.defaultOrder, options: DataSourceSubspaceWorkbookSortOptions, displaySortDropdown: true },
  'askDataUsageAnalytics': { defaultOrder: [], options: {}, displaySortDropdown: false },
};

const sortOptionsByFlowSubspace: { [s in FlowSubspaceName]: SortOptionsForType } = {
  'overview': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'connections': { defaultOrder: DataConnectionInFlowContentType.defaultOrder, options: FlowConnectionsSortOptions, displaySortDropdown: false },
  'scheduledTasks': { defaultOrder: [], options: {}, displaySortDropdown: false },
  'runHistory': { defaultOrder: FlowOutputStepRunContentType.defaultOrder, options: flowOutputStepRunSortOptions, displaySortDropdown: true },
  'lineage': { defaultOrder: [], options: {}, displaySortDropdown: false },
};

// exported so that it can be used directly in the omni box (search panel dropdown)
export const sortOptionsBySearchSubspace: { [s in SearchSubspaceName]: SortOptionsForType } = {
  'views': { defaultOrder: [Order.desc(OrderKeys.relevance)], options: viewSortOptions, displaySortDropdown: true },
  'metrics': { defaultOrder: MetricContentType.defaultOrder, options: metricsSortOptions, displaySortDropdown: true },
  'workbooks': { defaultOrder: [Order.desc(OrderKeys.relevance)], options: workbookSortOptions, displaySortDropdown: true },
  'projects': { defaultOrder: [Order.asc(OrderKeys.name)], options: projectSortOptions, displaySortDropdown: true },
  'datasources': { defaultOrder: [Order.desc(OrderKeys.isCertified), Order.desc(OrderKeys.relevance)], options: datasourceSortOptionsForSearch, displaySortDropdown: true },
  'flows': { defaultOrder: [Order.asc(OrderKeys.name)], options: flowSortOptions, displaySortDropdown: true },
  'dataRoles': { defaultOrder: DataRoleContentType.defaultOrder, options: dataRoleSortOptions, displaySortDropdown: true },
  'lenses': { defaultOrder: LensContentType.defaultOrder, options: lensSortOptions, displaySortDropdown: true },
  'databases': { defaultOrder: DatabaseContentType.defaultOrder, options: databaseDropdownSortOptions, displaySortDropdown: false },
  'tables': { defaultOrder: TableContentType.defaultOrder, options: tableDropdownSortOptions, displaySortDropdown: false },
  'databasesWithCrass': { defaultOrder: DatabaseWithCRASSSearchSubspaceType.defaultOrder, options: databaseWithCRASSSortOptions, displaySortDropdown: true },
  'tablesWithCrass': { defaultOrder: TableWithCRASSSearchSubspaceType.defaultOrder, options: tableWithCRASSSortOptions, displaySortDropdown: true },
  'users': { defaultOrder: defaultUserSort, options: userSortOptions, displaySortDropdown: false },
  'publishedConnections': { defaultOrder: PublishedConnectionContentType.defaultOrder, options: publishedConnectionSortOptions, displaySortDropdown: true },
};

function dfs(dictionary: { [s: string]: OptionDefinition }, predicate: (d: OptionDefinition) => boolean): OptionDefinition[] {
  const stack: OptionDefinition[] = _.values(dictionary);
  const parents: { [s: string]: OptionDefinition } = {};
  const seen: { [s: string]: boolean } = {};
  let found: OptionDefinition;

  while (stack.length > 0) {
    const v = stack.pop();
    if (predicate(v)) {
      found = v;
      break;
    }
    if (v.subSortOptions) {
      for (const child of _.values(v.subSortOptions)) {
        if (!seen[child.id]) {
          seen[child.id] = true;
          parents[child.id] = v;
          stack.push(child);
        }
      }
    }
  }

  if (found) {
    const path = [found];
    let parent = parents[found.id];
    while (parent) {
      path.unshift(parent);
      parent = parents[parent.id];
    }
    return path;
  } else {
    return [];
  }
}
